import { VolumeUpIcon } from "@heroicons/react/outline";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import FileListView from "./FileListView";
import { onDirectUpload } from "../../helpers";
import LoadingSpinner from "../LoadingSpinner";
import UploadErrors from "./UploadError";

type AudioDropZoneProps = {
  onUpload: any;
  isUploading: any;
};

export default function AudioDropZone({
  onUpload,
  isUploading,
}: AudioDropZoneProps) {
  const [file, setFile] = useState<any>(null);
  const [uploadProgress, setUploadProgress] = useState<number>();
  const { t } = useTranslation();
  const LocalBasePath = "page.audioUpload";
  //   TO USE FOR TRANSLATION:  {t(`${LocalBasePath}.`)} replace your key after dot
  const onDrop = useCallback(async (acceptedFiles) => {
    setUploadProgress(0);
    setFile(acceptedFiles[0]);
    onDirectUpload(acceptedFiles[0], (signedId: string) => {
      onUpload(signedId);
      /**
       * The upload progress functionality is not ready yet.
       * So, we set the progress status initially to (0%) and when the the file finishes uploading we set it to (100%).
       */
      setUploadProgress(100);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      onDrop,
      accept: [
        "audio/mp3",
        "audio/mpeg",
        "audio/wav",
        "audio/mpeg3",
        "audio/mpg",
        "audio/m4a",
        "audio/mp4",
        "audio/x-mp3",
        "audio/x-mpeg",
        "audio/x-mpeg3",
        "audio/x-mpg",
        "audio/x-m4a",
      ],
      maxFiles: 1,
      multiple: false,
      maxSize: 10 * 1024 * 1024, // 10 MegaBytes
    });

  useEffect(() => {
    if (!isNaN(uploadProgress!)) isUploading(uploadProgress! < 100);
  }, [uploadProgress]);

  const resetUploading = () => {
    setFile(null);
    setUploadProgress(undefined);
    onUpload(null);
  };

  const errorMessages: { [errorCode: string]: string } = {
    "file-too-large": t(`${LocalBasePath}.errorSizeFile`, { size: 10 }),
    "file-invalid-type": t(`${LocalBasePath}.errorFile`),
  };

  return (
    <div className="col-span-3">
      {!file && (
        <div
          {...getRootProps({ className: "dropzone" })}
          className="mt-1 border-2 border-gray-300 border-dashed rounded-md px-6 pt-5 pb-6 flex justify-center"
        >
          <input {...getInputProps()} />

          <div className="space-y-1 text-center">
            <VolumeUpIcon className="w-12 h-12 mx-auto text-gray-500" />
            <div className=" text-gray-600">
              <div className="flex justify-center">
                <h6 className="text-brandblue-500">
                  {t(`${LocalBasePath}.browse`) + " "}
                </h6>
                <h6 className="px-1"> {t(`${LocalBasePath}.dragDrop`)}</h6>
              </div>
              <h6>{isDragActive}</h6>
            </div>
            <p className="text-xs text-gray-500">
              {t(`${LocalBasePath}.audioRequirements`)}
            </p>
          </div>
        </div>
      )}
      <UploadErrors
        fileRejections={fileRejections}
        errorMessages={errorMessages}
      />
      {!!file && (
        <FileListView
          title={file.name}
          subTitle={file.type}
          color={"bg-brandblue-300"}
          icon={
            uploadProgress! >= 0 && uploadProgress! < 100 ? (
              <LoadingSpinner size={40} />
            ) : (
              <VolumeUpIcon className="w-10 h-10 mx-auto text-white" />
            )
          }
          onClose={resetUploading}
        />
      )}
    </div>
  );
}
